import {
  AlertCircleIcon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { CombinedIncomeStepData } from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/types";
import { useCombinedIncomeContext } from "../../../providers/useCombinedIncome";
import { getFullName } from "shared/utils/get-full-name";
import { ApplicantType } from "shared/types";
import { ReactNode } from "react";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { capitalize } from "shared/utils";

const formatCompanyDetails = (incomeData: CombinedIncomeStepData) => {
  const company = incomeData.company_name;
  const job = incomeData.job_title;
  if (company || job) return [company, job].filter(Boolean).join(", ");

  return "";
};

const Row = ({ label, value }: { label: ReactNode; value?: ReactNode }) => (
  <Show if={value}>
    <div className="flex items-center justify-between text-sm font-medium text-content-500">
      {typeof label === "string" ? <p className="w-fit">{label}</p> : label}
      {typeof value === "string" ? (
        <p className="text-right">{value}</p>
      ) : (
        value
      )}
    </div>
  </Show>
);

export const ReviewNewIncomeSource = ({
  newIncomeData,
  cents,
  onClose,
  onBack,
  onSubmit,
  isSubmitting
}: {
  newIncomeData: CombinedIncomeStepData;
  cents?: number;
  onClose: () => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
}) => {
  const { applicant } = useCombinedIncomeContext();

  const applicantName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });
  const applicantRole = capitalize(applicant.role ?? ApplicantType.Applicant);
  const companyDetails = formatCompanyDetails(newIncomeData);

  return (
    <div className="flex flex-col h-full">
      <header className="p-6 border-b border-alpha-100 flex justify-between">
        <div>
          <h1 className="text-xl text-content-1000 font-bold mb-2">
            Confirm verification
          </h1>
          <p className="font-medium text-content-400 text-sm">
            Please make sure everything is correct before proceeding
          </p>
        </div>
        <IconButton
          HugeIcon={Cancel01Icon}
          size={IconButtonSize.SM}
          borderLess
          className="-mt-2 -mr-2 text-content-500"
          onClick={onClose}
        />
      </header>

      <div className="px-6 py-[28px] h-[488px]">
        <div className="space-y-2.5">
          <Row
            label="Uploaded documents"
            value={String(newIncomeData.documents?.length ?? 0) + "x"}
          />
          <Row
            label="Document type"
            value={capitalize(
              newIncomeData.currentDocumentType ?? ""
            ).replaceAll("_", " ")}
          />
          <Row label="Person" value={`${applicantName}, ${applicantRole}`} />
          <Row label="Income source" value={companyDetails} />
        </div>
        <hr className="my-6 text-alpha-40" />
        <Row
          label={<p className="font-bold text-base">Verification cost</p>}
          value={
            <span className="ml-2 py-0.5 px-2 bg-alpha-40 rounded-full text-content-700 text-sm font-bold">
              {cents ? `$${(cents / 100).toFixed(2)}` : "-"}
            </span>
          }
        />
        <hr className="my-6 text-alpha-40" />
        <Banner
          variant="warning"
          HugeIcon={
            <HugeiconsIcon
              icon={AlertCircleIcon}
              className="text-warning-500"
              size={20}
            />
          }
          body="You will be charged an additional fee once the verification is successfully completed"
          bodyClassName="text-warning-700"
        />
      </div>

      <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end w-full">
        <Button className="w-[125px]" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>

        <Button
          className="w-[125px]"
          variant={ButtonVariant.PRIMARY}
          loading={isSubmitting}
          onClick={onSubmit}
        >
          Verify
        </Button>
      </div>
    </div>
  );
};
